import useTranslator from "../../hooks/useTranslator";
import {
  getUserDisplayName,
  getCustomIncomeDetails,
  haveValue,
  convertCurrencyFormat,
  resolveIncomeSelection,
} from "../../helper/common";
import Arrow from "../../assets/img/dark-arrow.svg";
import userIcon from "../../assets/img/user-icon.svg";
import { useEffect, useMemo, useRef, useState } from "react";

const CandidateSummary = ({ originalUser, allUsers, definedRent }) => {
  const { _t } = useTranslator();
  const getIncome = (_user, incomeSource) => {
    const fallbackIncome = _user?.useFallbackIncome;
    const useFallbackIncome =
      typeof fallbackIncome === "boolean" ? fallbackIncome : true;

    return getCustomIncomeDetails(
      _user,
      originalUser,
      _t,
      incomeSource,
      undefined,
      //   useFallbackIncome,
      true,
      false,
      _user?.custom_income_sources
    );
  };

  const getEffortRatePercentage = (combinedIncome) => {
    if (!haveValue(combinedIncome) || combinedIncome === 0)
      return _t("noIncome");
    else
      return `${Math.ceil(
        (+definedRent /
          (haveValue(combinedIncome) && combinedIncome != 0
            ? combinedIncome
            : definedRent)) *
          100
      )}%`;
  };

  const tenantIncomes = useMemo(() => {
    return (
      allUsers?.map((user) => {
        const user_income_sources = resolveIncomeSelection(
          user?.income_selection,
          user?.tenant_id?._id
        );
        return getIncome(user?.tenant_id, user_income_sources?.tenant_source);
      }) || []
    );
  }, [allUsers]);

  const guarantorRows = useMemo(() => {
    const seenGuarantorIds = new Set();

    return (allUsers || []).flatMap((tenantUser, tenantIndex) => {
      const guarantors = tenantUser?.tenant_id?.gurantor_id || [];

      return guarantors
        .map((guarantor, guarantorIndex) => ({
          guarantor,
          guarantorIndex,
          tenantIndex,
          tenantUser,
        }))
        .filter(({ guarantor }) => {
          const guarantorId = guarantor?._id?.toString();

          if (!haveValue(guarantorId)) {
            return true;
          }

          if (seenGuarantorIds.has(guarantorId)) {
            return false;
          }

          seenGuarantorIds.add(guarantorId);
          return true;
        });
    });
  }, [allUsers]);

  const guarantorIncomes = useMemo(() => {
    return guarantorRows.map(({ guarantor, tenantUser }) => {
      const user_income_sources = resolveIncomeSelection(
        tenantUser?.income_selection,
        tenantUser?.tenant_id?._id
      );
      const guarantor_income_sources =
        user_income_sources?.[tenantUser?.tenant_id?._id]?.guarantor_sources ??
        user_income_sources?.guarantor_sources;
      return getIncome(guarantor, guarantor_income_sources?.[guarantor?._id]);
    });
  }, [guarantorRows]);

  const allTenantIncome = tenantIncomes.reduce(
    (sum, u) => sum + (+u?.amount || 0),
    0
  );
  const allGuarantorIncome = guarantorIncomes.reduce(
    (sum, u) => sum + (+u?.amount || 0),
    0
  );

  const containerRefs = useRef([]);
  const [wrapped, setWrapped] = useState([]);

  useEffect(() => {
    const viewportWidth = window.innerWidth;
    const thresholdPx = (100 / 1440) * viewportWidth;
    const newHeights = containerRefs.current.map(
      (ref) => ref?.offsetHeight || 0
    );

    const newWrapped = newHeights.map((height) => height > thresholdPx);
    setWrapped(newWrapped);
  }, [allUsers]);

  const guarantorsContainerRefs = useRef([]);
  const [guarantorsWrapped, setGuarantorsWrapped] = useState([]);

  useEffect(() => {
    const viewportWidth = window.innerWidth; // current screen width
    const thresholdPx = (100 / 1440) * viewportWidth;
    const newHeights = guarantorsContainerRefs.current.map(
      (ref) => ref?.offsetHeight || 0
    );

    const newWrapped = newHeights.map((height) => height > thresholdPx);
    setGuarantorsWrapped(newWrapped);
  }, [allUsers]);

  return (
    <>
      <div className="candidate-summary-table">
        <div className="candidate-summary-report-header">
          <div className="candidate-summary-report-title">
            <div className="candidate-summary-report-icon-person">
              <img src={userIcon} alt="" />
            </div>
            <h2>{_t("tenants")}</h2>
          </div>
          <div className="candidate-summary-report-effort-rate">
            <span className="candidate-summary-report-label">
              {_t("combinedEffortRate")}
            </span>
            <span className="candidate-summary-report-rate-box">
              {getEffortRatePercentage(allTenantIncome)}
            </span>
          </div>
        </div>

        {allUsers?.map((user, index) => (
          <div
            key={index}
            ref={(el) => (containerRefs.current[index] = el)}
            className={`candidate-summary-report-tenant-row ${
              wrapped[index] && "flex-wrap"
            }`}
          >
            <div className="candidate-summary-report-tenant-info">
              <img src={Arrow} alt="arrow" />
              <span className="candidate-summary-report-name">
                <span>{getUserDisplayName(user?.tenant_id)}</span>{" "}
              </span>
              <span className="candidate-summary-report-tag">
                {_t("tenant")} {index + 1}
              </span>
            </div>
            {(() => {
              const incomeSources = resolveIncomeSelection(
                user?.income_selection,
                user?.tenant_id?._id
              );
              const userData = getIncome(
                user?.tenant_id,
                incomeSources?.tenant_source
              );
              if (!Array.isArray(userData?.source)) return null;

              return (
                <>
                  <div
                    className={`candidate-summary-report-income-details ${
                      wrapped[index] && "order-2 m-0"
                    }`}
                  >
                    <div className="candidate-summary-report-label">
                      {_t("incomeSource")}
                    </div>
                    {userData.source.map((src, idx) => {
                      if (src?.startsWith?.("payslip:")) {
                        const [label, rawDate] = src.split(":");
                        const parts = rawDate.trim().split(" ");

                        let month = parts[0].replace(",", "");
                        const hasComma = parts[0].includes(",");
                        const rest = parts.slice(1).join(" ");

                        return (
                          <span
                            key={idx}
                            className="candidate-summary-report-source-tag"
                          >
                            {_t(label)}: {_t(month)}
                            {hasComma ? "," : ""}
                            {rest ? " " + rest : ""}
                          </span>
                        );
                      }

                      return (
                        <span
                          key={idx}
                          className="candidate-summary-report-source-tag"
                        >
                          {_t(src)}
                        </span>
                      );
                    })}
                  </div>

                  <span
                    className={`candidate-summary-report-income-box ${
                      wrapped[index] && "order-1"
                    }`}
                  >
                    {_t("income")}:{" "}
                    {convertCurrencyFormat(userData?.amount ?? 0)} €
                  </span>
                </>
              );
            })()}
          </div>
        ))}
      </div>

      {guarantorRows.length > 0 && (
        <div className="candidate-summary-table">
          <div className="candidate-summary-report-header">
            <div className="candidate-summary-report-title">
              <div className="candidate-summary-report-icon-person">
                <img src="/images/guarantor-icon.svg" alt="" />
              </div>
              <h2>{_t("guarantors")}</h2>
            </div>
            <div className="candidate-summary-report-effort-rate">
              <span className="candidate-summary-report-label">
                {_t("combinedEffortRate")}
              </span>
              <span className="candidate-summary-report-rate-box">
                {" "}
                {getEffortRatePercentage(allGuarantorIncome)}
              </span>
            </div>
          </div>

          {guarantorRows.map(
            ({ guarantor, guarantorIndex, tenantIndex, tenantUser }, index) => (
              <div
                key={guarantor?._id ?? `${tenantIndex}-${guarantorIndex}`}
                ref={(el) => (guarantorsContainerRefs.current[index] = el)}
                className={`candidate-summary-report-tenant-row-without-border ${
                  index === guarantorRows.length - 1
                    ? "candidate-summary-border-bottom"
                    : ""
                }
                    ${guarantorsWrapped[index] && "flex-wrap"}
                `}
              >
                <div className="candidate-summary-report-tenant-info">
                  <img src={Arrow} alt="arrow" />
                  <span className="candidate-summary-report-name">
                    <span>{getUserDisplayName(guarantor)}</span>{" "}
                  </span>
                  <span className="candidate-summary-report-tag">
                    {` ${_t("guarantor")} ${guarantorIndex + 1} of ${_t(
                      "tenant"
                    )} ${tenantIndex + 1}`}
                  </span>
                </div>
                {(() => {
                  const incomeSources = resolveIncomeSelection(
                    tenantUser?.income_selection,
                    tenantUser?.tenant_id?._id
                  );
                  const guarantor_income_sources =
                    incomeSources?.[tenantUser?.tenant_id?._id]
                      ?.guarantor_sources ?? incomeSources?.guarantor_sources;
                  //   const userData = getIncome(
                  //     guarantor,
                  //     incomeSources?.guarantor_sources
                  //       ? incomeSources?.guarantor_sources?.[guarantor?._id]
                  //       : tenantUser?.income_selection?.guarantor_sources?.[
                  //           guarantor?._id
                  //         ]
                  //   );
                  const userData = getIncome(
                    guarantor,
                    guarantor_income_sources?.[guarantor?._id]
                  );
                  if (!Array.isArray(userData?.source)) return null;

                  return (
                    <>
                      <div
                        className={`candidate-summary-report-income-details ${
                          guarantorsWrapped[index] && "order-2 m-0"
                        }`}
                      >
                        <div className="candidate-summary-report-label">
                          {_t("incomeSource")}
                        </div>
                        {userData.source.map((src, idx) => {
                          if (src?.startsWith?.("payslip:")) {
                            const [label, rawDate] = src.split(":");
                            const parts = rawDate.trim().split(" ");

                            let month = parts[0].replace(",", "");
                            const hasComma = parts[0].includes(",");
                            const rest = parts.slice(1).join(" ");

                            return (
                              <span
                                key={idx}
                                className="candidate-summary-report-source-tag"
                              >
                                {_t(label)}: {_t(month)}
                                {hasComma ? "," : ""}
                                {rest ? " " + rest : ""}
                              </span>
                            );
                          }

                          return (
                            <span
                              key={idx}
                              className="candidate-summary-report-source-tag"
                            >
                              {_t(src)}
                            </span>
                          );
                        })}
                      </div>

                      <span
                        className={`candidate-summary-report-income-box ${
                          guarantorsWrapped[index] && "order-1"
                        }`}
                      >
                        {_t("income")}: {userData?.amount ?? 0} €
                      </span>
                    </>
                  );
                })()}
              </div>
            )
          )}
        </div>
      )}
    </>
  );
};

export default CandidateSummary;
